import Layout from 'Nori/Layout';
import HeroFull from 'Nori/Hero/HeroFull';
import RichText from 'Custom/RichText';
import SplitContent from 'Custom/Split/SplitContent';
import BannerCTA from '../components/Custom/BannerCTA';
import AjustableImage from 'Custom/AjustableImage';
import ItemBreakpoint from 'Custom/ItemBreakpoint';

import PLUS_IMAGE from 'Images/misc/plus-sign-desktop.webp';
import QUESTION_MARK from 'Images/misc/question-mark.webp';
import QUESTION_MARK_MOBILE from 'Images/misc/icon-question-mobile.webp';
import WHITE_MARBLE_BG_IMAGE from 'Images/misc/common-ques-desktop.png';
import { FAQ_USERS_2_LIST } from 'Utils/questions';
import HERO_BG_IMAGE from 'Images/background/dark-blue-desktop.png';
import HERO_BG_IMAGE_MOBILE from 'Images/background/blue-bg-mobile.png';
import styles from 'styles/pages/medicineInteractions.module.scss';

const HEAD = {
  title:
    'Medicine Interactions with NEXPLANON® (etonogestrel implant) 68 mg Radiopaque',
  description:
    'Discuss your medicines with your doctor before getting NEXPLANON® (etonogestrel implant) 68 mg Radiopaque and learn about possible drug interactions.',
  keywords:
    'Nexplanon medicine interactions, Nexplanon drug interactions, Nexplanon and other medications',
};

const CTA_BUTTON = {
  showModal: true,
  href: '/',
  label: 'FDA MedWatch',
  target: '_self',
};

const HeroBannerContent = () => {
  return (
    <div className={styles['content-wrap']} data-qa-tag="hero-content">
      <RichText data-qa-tag="hero-text">
        <div
          className={styles.medicineInteractionsPage}
          data-qa-tag="medicine-interactions-hero"
        >
          <h4 data-qa-tag="hero-title">MEDICINE INTERACTIONS</h4>
          <p className={styles.middleHeroText} data-qa-tag="hero-subtitle">
            Talk to your doctor about all the medicines you take
          </p>
          <p className={styles.bottomHeroText} data-qa-tag="hero-content">
            Be sure to inform your doctor about prescription medicines,
            non-prescription medicines, vitamins, and/or herbal products you
            take.
          </p>
        </div>
      </RichText>
    </div>
  );
};

export default function MedicineInteractions() {
  return (
    <Layout
      head={HEAD}
      className={styles.medicineInteractions}
      data-design-visible="visible_vd"
      data-design-type="module"
      data-design-nori_id="Hero"
      data-design-module_name="MEDICINE INTERACTIONS"
      data-design-module_no="01"
      data-qa-tag="hero-medicine-interactions"
    >
      <div className={styles.heroWrapper} data-qa-tag="hero-wrapper">
        <ItemBreakpoint breakpoint={'lg'}>
          <HeroFull image={HERO_BG_IMAGE} data-qa-tag="hero-desktop-image">
            <HeroBannerContent></HeroBannerContent>
          </HeroFull>
          <HeroFull
            image={HERO_BG_IMAGE_MOBILE}
            data-qa-tag="hero-mobile-image"
          >
            <HeroBannerContent></HeroBannerContent>
          </HeroFull>
        </ItemBreakpoint>
      </div>

      <SplitContent
        data-design-visible="visible_vd"
        data-design-type="module"
        data-design-nori_id="Inline Content"
        data-design-module_name="Tell your doctor about all the medicines you take"
        data-design-module_no="02"
        data-qa-tag="mi-tell-your-doctor"
        className={styles.tellYourDoctor}
      >
        <AjustableImage
          size="full"
          image={{
            src: PLUS_IMAGE,
            style: { maxWidth: 292, height: 'auto' },
            alt: 'plus-sign',
          }}
          imageMobile={{
            src: PLUS_IMAGE,
            style: { maxWidth: 146, height: 'auto' },
            alt: 'plus-sign',
          }}
          data-qa-tag="splitContent-plus-image"
        />
        <>
          <RichText data-qa-tag="mi-tell-your-doctor-richText">
            <h2 data-qa-tag="mi-tell-your-doctor-heading">
              Tell your doctor about all the medicines you take
            </h2>
            <p data-qa-tag="mi-tell-your-doctor-content">
              That includes prescription medicines, non-prescription medicines,
              vitamins, and/or herbal supplements. Certain medicines may make
              NEXPLANON less effective, including:
            </p>
            <ItemBreakpoint>
              <>
                {FAQ_USERS_2_LIST && (
                  <div
                    className={styles.splittedList}
                    data-qa-tag="mi-tell-your-doctor-medicines-list"
                  >
                    <ul
                      className="primary"
                      data-qa-tag="mi-tell-your-doctor-list1"
                    >
                      {FAQ_USERS_2_LIST.slice(0, 7).map((item, i) => (
                        <li
                          key={`me-1${i}`}
                          data-qa-tag="mi-tell-your-doctor-list-item"
                        >
                          {item}
                        </li>
                      ))}
                    </ul>
                    <ul
                      className="primary"
                      data-qa-tag="mi-tell-your-doctor-list2"
                    >
                      {FAQ_USERS_2_LIST.slice(7, 14).map((item, i) => (
                        <li
                          key={`me-2-${i}`}
                          data-qa-tag="mi-tell-your-doctor-list-item"
                        >
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
              <div
                className={styles.mobileList}
                data-qa-tag="mi-tell-your-doctor-mobile-medicines-list"
              >
                {FAQ_USERS_2_LIST && (
                  <ul
                    className="primary"
                    data-qa-tag="mi-tell-your-doctor-mobile-list"
                  >
                    {FAQ_USERS_2_LIST.map((item, i) => (
                      <li
                        key={`me-single${i}`}
                        data-qa-tag="mi-tell-your-doctor-mobile-list-item"
                      >
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </ItemBreakpoint>
          </RichText>
        </>
      </SplitContent>

      <SplitContent
        reverse
        data-design-visible="visible_vd"
        data-design-type="module"
        data-design-nori_id="Inline Content"
        data-design-module_name="Taking something else besides what's listed above? Talk to your doctor"
        data-design-module_no="03"
        className={styles.talkToYourDoctor}
        data-qa-tag="mi-talk-your-doctor"
      >
        <AjustableImage
          size="full"
          image={{
            src: QUESTION_MARK,
            style: { maxWidth: 292, height: 'auto' },
            alt: 'question-mark',
          }}
          imageMobile={{
            src: QUESTION_MARK_MOBILE,
            style: { width: 'auto', height: 146 },
            alt: 'question-mark',
          }}
          data-qa-tag="splitContent-question-image"
        />
        <>
          <RichText data-qa-tag="mi-talk-your-doctor-richText">
            <h2 data-qa-tag="mi-talk-your-doctor-heading">
              Taking something else besides what's listed above? Talk to your
              doctor
            </h2>
            <p data-qa-tag="mi-talk-your-doctor-content1">
              If you are taking medicines or herbal products that might make
              NEXPLANON less effective, you and your doctor may decide to leave
              NEXPLANON in place; in that case, an additional, non-hormonal
              contraceptive should be used. Because the effect of another
              medicine on NEXPLANON may last up to 28 days after stopping the
              medicine, it is necessary to use the additional, non-hormonal
              contraceptive for that long.
            </p>
            <p data-qa-tag="mi-talk-your-doctor-content2">
              When you're using NEXPLANON, tell all healthcare professionals
              that you have NEXPLANON in place in your arm.
            </p>
          </RichText>
        </>
      </SplitContent>

      <BannerCTA
        button={CTA_BUTTON}
        moduleNo="04"
        color="marble"
        moduleName="Report negative side effects of prescription drugs to the FDA"
        className={styles.reportToFda}
        style={{
          '--desktop-bg': `url(${WHITE_MARBLE_BG_IMAGE.src})`,
        }}
        data-qa-tag="mi-report-to-fda"
      >
        <>
          <h2 data-qa-tag="mi-report-to-fda-heading">
            Report negative side effects of prescription drugs to the FDA
          </h2>
          <p className="intro-sm" data-qa-tag="mi-report-to-fda-content">
            You are encouraged to report negative side effects of prescription
            drugs to the US Food and Drug Administration (FDA).
          </p>
        </>
      </BannerCTA>
    </Layout>
  );
}
